import { LitElement, html, nothing } from "lit";

export class ReportClient extends LitElement {
  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.memberQuery = "";
    this.showCardInfo = false;
    this.searchResults = [];
    this.searchFilter = "";
    this.isLoading = false;
    this.page = 0;
    this.pageSize = 1;
  }

  static get properties() {
    return {
      memberQuery: { type: String },
      showCardInfo: { type: Boolean },
      searchResults: { type: Array },
      searchFilter: { type: String },
      isLoading: { type: Boolean },
      page: { type: Number },
      pageSize: { type: Number },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.calculatePageSize();
    window.addEventListener("resize", this.calculatePageSize.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.calculatePageSize.bind(this));
  }

  calculatePageSize() {
    const windowHeight = window.innerHeight;
    const usableHeight = windowHeight * 0.7;

    const inputHeight = 100;
    const paginationHeight = 70;
    const tableHeaderHeight = 50;
    const rowHeight = 55;

    const remaining =
      usableHeight - (inputHeight + paginationHeight + tableHeaderHeight);
    const rowsFit = Math.floor(remaining / rowHeight);

    this.pageSize = Math.max(rowsFit, 1);
    this.requestUpdate();
  }

  parseSearchInput(input) {
    const cleanInput = input.trim().replace(/\s+/g, " ");
    const parts = cleanInput.split(" ");

    let nameParts = [];
    let zip = "";
    let email = "";
    let cell = "";
    let person_pk = "";

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const zipPattern = /^\d{5}(-\d{4})?$/;
    const phonePattern =
      /^\d{10}$|^\+?\d{1,4}?[-.\s]?(\(?\d{3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
    const personPkPattern = /^\d{8}$/;

    for (const part of parts) {
      const digitsOnly = part.replace(/\D/g, "");

      if (!person_pk && digitsOnly.length === 8 && /^\d{8}$/.test(digitsOnly)) {
        person_pk = digitsOnly;
      } else if (!cell && digitsOnly.length === 10) {
        cell = digitsOnly;
      } else if (!email && emailPattern.test(part)) {
        email = part;
      } else if (!zip && zipPattern.test(part)) {
        zip = part;
      } else {
        nameParts.push(part);
      }
    }

    if (person_pk) {
      nameParts = [];
      zip = "";
      email = "";
      cell = "";
    }
    return {
      name: nameParts.length ? nameParts.join(" ") : "",
      zip,
      email,
      cell,
      person_pk,
      card_info: this.showCardInfo,
    };
  }

  async handleSearchMember() {
    if (!this.memberQuery.trim()) {
      alert("Please enter a search term.");
      return;
    }

    const parsed = this.parseSearchInput(this.memberQuery);
    console.log("Parsed search input:", parsed);
    const token = window.authmgr?.current_login?.id_token;
    if (!token) {
      alert("You're not logged in. Please sign in to continue.");
      return;
    }

    const isValidSearch =
      parsed.name ||
      parsed.zip ||
      parsed.email ||
      parsed.cell ||
      parsed.person_pk;

    if (!isValidSearch) {
      alert(
        "Please enter full name, zip, and either email or phone — or enter Person PK."
      );
      return;
    }

    this.isLoading = true;

    try {
      const res = await fetch(
        "https://ud642cj7y4.execute-api.us-east-1.amazonaws.com/prod/api/process",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(parsed),
        }
      );

      const text = await res.text();

      if (!res.ok) {
        console.error("Backend error:", text);
        this.searchResults = [];
        if (res.status === 404 || res.status === 400) {
          alert("We couldn't find any matching members.");
        } else {
          alert("Something went wrong. Please try again later.");
        }
        return;
      }

      const data = JSON.parse(text);
      console.log("Parsed result:", data);
      this.searchResults = Array.isArray(data) ? data : [data];
      this.page = 0;
      if (this.searchResults.length === 0) {
        alert("We couldn't find any matching members.");
      }
    } catch (err) {
      console.error("Search error:", err);
      alert("Something went wrong while fetching info.");
      this.searchResults = [];
    } finally {
      this.isLoading = false;
    }
  }

  nextPage() {
    if ((this.page + 1) * this.pageSize < this.searchResults.length) {
      this.page++;
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
    }
  }

  render() {
    const paginatedResults = this.searchResults.slice(
      this.page * this.pageSize,
      (this.page + 1) * this.pageSize
    );

    return html`
      <style>
        .panel {
          font-family: sans-serif;
          width: 80vw;
          margin: auto;
        }
        table {
          width: 95%;
          border-collapse: collapse;
          margin-top: 16px;
        }
        th,
        td {
          padding: 8px;
          border: 1px solid #ccc;
          text-align: left;
        }
        th {
          background-color: #f0f0f0;
        }
        .pagination {
          text-align: center;
          margin-top: 12px;
        }
        .pagination button {
          margin: 0 8px;
        }
        img[alt="✓"] {
          width: 16px;
          height: 16px;
          margin-left: 6px;
        }
        .match-text {
          color: green;
          font-weight: bold;
        }
      </style>

      <div class="panel" id="search-panel">
        <div class="panel-header">
          <h1>Search Member</h1>
        </div>

        <div
          id="search-controls"
          style="display: flex; gap: 12px; align-items: center; margin: 24px;"
        >
          <input
            type="text"
            placeholder="Enter Full Name, Zipcode, Email, Phone Number OR Person PK"
            class="date-input"
            style="min-width: 600px; flex-grow: 1; font-size: 16px; height: 48px; padding: 10px 16px;"
            .value=${this.memberQuery}
            @input=${(e) => (this.memberQuery = e.target.value)}
            @keypress=${(e) => {
              if (e.key === "Enter") this.handleSearchMember();
            }}
          />
          <button
            @click=${this.handleSearchMember}
            ?disabled=${this.isLoading}
            style="min-width: 100px;"
          >
            ${this.isLoading ? "Searching..." : "Search"}
          </button>
        </div>

        ${this.searchResults.length > 0
          ? html`
              <table>
                <thead>
                  <tr>
                    ${["Match", "Name", "Contact Info", "Membership"].map(
                      (header) => html`<th>${header}</th>`
                    )}
                  </tr>
                </thead>
                <tbody>
                  ${paginatedResults.map((item) => {
                    const match = item.matchCategory;
                    const matchIcon =
                      match === "full" ? "/mobile.png" : "/no-mobile.png";

                    return html`
                      <tr
                        style=${match === "full"
                          ? "background-color: #d4edda;"
                          : ""}
                      >
                        <td>
                          <div
                            style="display: flex; flex-direction: column; align-items: center;"
                          >
                            <img src=${matchIcon} width="40" height="40" />
                            ${!item.has_score
                              ? html`
                                  <img
                                    src="/meilisearch.png"
                                    alt="MeiliSearch"
                                    width="20"
                                    height="20"
                                    style="margin-top: 4px;"
                                  />
                                `
                              : nothing}
                          </div>
                        </td>
                        <td>
                          <div style="font-weight: bold; font-size: 14px;">
                            ${item.match_name
                              ? html`<span class="match-text">
                                  ${item.name?.first_nm || ""}
                                  ${item.name?.last_nm || ""}
                                </span>`
                              : html`
                                  ${item.name?.first_nm || ""}
                                  ${item.name?.last_nm || ""}
                                `}
                          </div>
                          <div style="font-size: 10px; margin-top: 4px;">
                            ${item.name.person_pk
                              ? html`<div>${item.name.person_pk}</div>`
                              : nothing}
                            ${item.name.gender
                              ? html`<div>Gender: ${item.name.gender}</div>`
                              : nothing}
                            ${item.name.age
                              ? html`<div>Age: ${item.name.age}</div>`
                              : nothing}
                          </div>
                        </td>
                        <td>
                          ${(item.addresses || []).map(
                            (addr) => html`
                              <div>
                                ${addr.addr1}, ${addr.city}, ${addr.territory}
                                ${addr.match
                                  ? html`<span class="match-text"
                                      >${addr.zipcode}</span
                                    >`
                                  : html`${addr.zipcode}`}
                              </div>
                            `
                          )}
                          ${(item.phones || []).map(
                            (p) => html`
                              <div>
                                ${p.match
                                  ? html`<span class="match-text"
                                      >${p.area_code}-${p.local_no}</span
                                    >`
                                  : html`${p.area_code}-${p.local_no}`}
                              </div>
                            `
                          )}
                          ${(item.emails || []).map(
                            (e) => html`
                              <div>
                                ${e.match
                                  ? html`<span class="match-text"
                                      >${e.person_email_addr}</span
                                    >`
                                  : html`${e.person_email_addr}`}
                              </div>
                            `
                          )}
                        </td>

                        <td>
                          ${(item.memberships || []).map(
                            (m) => html`
                              <div>
                                Status: ${m.mbr_status}, Type: ${m.mbr_type},
                                AffPk: ${m.aff_pk}
                              </div>
                            `
                          )}
                        </td>
                      </tr>
                    `;
                  })}
                </tbody>
              </table>

              <div class="pagination">
                <button @click=${this.prevPage} ?disabled=${this.page === 0}>
                  Previous
                </button>
                <span>
                  Page ${this.page + 1} of
                  ${Math.ceil(this.searchResults.length / this.pageSize)}
                </span>
                <button
                  @click=${this.nextPage}
                  ?disabled=${(this.page + 1) * this.pageSize >=
                  this.searchResults.length}
                >
                  Next
                </button>
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

window.customElements.define("report-client", ReportClient);
